import type {
  CategoriaComContrato,
  ContractCategory,
  EstadoCriacao,
  MensagemBloqueioCriacao,
  ModalidadeCobrancaArbitral,
  ResolverEstadoCriacaoInput,
} from './types';
import { flexoesDaCategoria, nomeDaCategoria, unidadeDaCategoria } from './utils/rotulosCategoria';

/**
 * Só a Arbitragem cria sem contrato: o processo nasce com custa avulsa, cobrada depois da
 * triagem (regra de produto de 13/10, A-23 do smoke da v560). Notificação e Negociação
 * continuam exigindo contrato.
 */
export function podeCriarSemContrato(categoria: ContractCategory): boolean {
  return categoria === 'ARBITRAGEM';
}

/**
 * Regra única de "pode criar agora?". A ordem importa: enquanto o contrato não foi verificado,
 * ou a verificação falhou, nada é afirmado sobre ele, nem que falta. Trial e ciclo só barram
 * quem tem contrato: os selectors que alimentam esses campos já exigem `hasActive`.
 */
export function resolverEstadoCriacao({
  categoria,
  contratoResolvido,
  erroContrato,
  hasActive,
  trialBloqueado,
  limiteTeste,
  limiteCiclo,
}: ResolverEstadoCriacaoInput): EstadoCriacao {
  if (!contratoResolvido) return 'CARREGANDO';
  if (erroContrato) return 'ERRO_VERIFICACAO';
  if (!hasActive && !podeCriarSemContrato(categoria)) return 'SEM_CONTRATO';
  if (trialBloqueado) return 'BLOQUEADO_TESTE';
  if (limiteTeste) return 'LIMITE_TESTE';
  if (limiteCiclo) return 'LIMITE_CICLO';
  return 'LIVRE';
}

const MENSAGENS: Record<
  Exclude<EstadoCriacao, 'LIVRE' | 'SEM_CONTRATO'>,
  MensagemBloqueioCriacao
> = {
  CARREGANDO: {
    titulo: 'Carregando seu plano',
    descricao: 'Aguarde a verificação do seu plano para enviar.',
    tom: 'aviso',
    dicaBotao: 'Carregando seu plano...',
  },
  ERRO_VERIFICACAO: {
    titulo: 'Não foi possível verificar seu plano',
    descricao: 'Não foi possível verificar seu plano agora. Recarregue a página e tente de novo.',
    tom: 'erro',
    dicaBotao: 'Não foi possível verificar seu plano agora. Recarregue a página e tente de novo.',
  },
  BLOQUEADO_TESTE: {
    titulo: 'Produto bloqueado durante o trial',
    descricao:
      'Este produto está configurado para bloquear uso durante o período de testes. Converta o trial para começar a usar.',
    tom: 'erro',
  },
  LIMITE_TESTE: {
    titulo: 'Limite do período de testes',
    descricao: 'Você chegou ao seu limite do período de testes. Converta o trial para continuar.',
    tom: 'erro',
  },
  LIMITE_CICLO: {
    titulo: 'Limite do ciclo atingido',
    descricao: 'Você usou todo o saldo deste ciclo. O limite renova no próximo ciclo.',
    tom: 'erro',
    dicaBotao: 'Você atingiu o limite do seu pacote neste ciclo. O limite renova no próximo ciclo.',
  },
};

function mensagemSemContrato(categoria: CategoriaComContrato): MensagemBloqueioCriacao {
  const { novos } = flexoesDaCategoria(categoria);
  return {
    titulo: `Você não tem um plano de ${nomeDaCategoria(categoria)}`,
    descricao: `Para criar ${novos} ${unidadeDaCategoria(categoria)}, contrate o produto.`,
    tom: 'erro',
  };
}

export function mensagemDoBloqueio(
  estado: Exclude<EstadoCriacao, 'LIVRE'>,
  categoria: CategoriaComContrato,
): MensagemBloqueioCriacao {
  if (estado === 'SEM_CONTRATO') return mensagemSemContrato(categoria);
  return MENSAGENS[estado];
}

/**
 * Espelha a regra do core que carimba a cobrança do processo na criação: contrato ACTIVE ou
 * TRIAL dá convênio (`PER_BATCH`), qualquer outra situação dá custa avulsa (`PER_PROCESS`).
 * Ver `contractCheckHelpers.ts:114` (`hasActive`) e `arbitralConvenioService.ts:92–106`
 * (`resolverBillingSnapshot`) no core.
 *
 * Não se deriva da `origem` do preço: contrato ativo sem condição comercial devolve preço de
 * TABELA e continua sendo convênio. Enquanto o contrato não foi verificado, ou a verificação
 * falhou, devolve `null`: a tela não afirma modalidade nenhuma.
 */
export function modalidadeDeCobrancaArbitral({
  contratoResolvido,
  erroContrato,
  hasActive,
}: Pick<
  ResolverEstadoCriacaoInput,
  'contratoResolvido' | 'erroContrato' | 'hasActive'
>): ModalidadeCobrancaArbitral | null {
  if (!contratoResolvido || erroContrato) return null;
  return hasActive ? 'CONVENIO' : 'AVULSO';
}

/**
 * D-05 (14/10): o "Criar em lote" ainda é mock. Ele anuncia "N processo(s) criado(s)" e
 * injeta processos falsos na lista pelo localStorage, então a custa avulsa não se estende ao
 * lote: sem convênio só aparece "Criar processo" (o lote de verdade é a web#1295).
 * Modalidade `null` (contrato carregando ou em erro) também esconde: a Sidebar navega direto
 * enquanto carrega, e o wizard do lote não consulta o contrato antes de criar.
 */
export function podeCriarLoteArbitral(modalidade: ModalidadeCobrancaArbitral | null): boolean {
  return modalidade === 'CONVENIO';
}
